// PozicovnaMobile.jsx — Matrix v2 mobilný modul Požičovňa (ústava 58.8 — Marek)
// Veľké tlačidlá (ústava 60.1 MOZOG), zoznam položiek + Vypožičať/Vrátiť.
import React, { useState, useEffect, useMemo } from 'react'
import { COLORS, TYPO, SPACE, RADIUS, SK, todayLocal, hladaj } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'

const STATUS_COLOR = {
  dostupne:  COLORS.add,
  pozicane:  '#f59e0b',
  v_servise: '#f59e0b',
  vyradene:  COLORS.muted,
}
const STATUS_LABEL = {
  dostupne:  'Dostupné',
  pozicane:  'Požičané',
  v_servise: 'V servise',
  vyradene:  'Vyradené',
}

// ks-polozky (na mnozstvo): backend status neprepina na 'pozicane' (UPDATE ma WHERE tracking='kus') —
// efektivny stav sa odvodi z available_ks/loaned_ks z GET /api/loan-items (oprava 2026-09-14, OH.01/OH.02)
const effStatus = r => (r && r.tracking === 'ks' && r.status === 'dostupne' && (Number(r.loaned_ks) || 0) > 0 && (Number(r.available_ks) || 0) <= 0) ? 'pozicane' : ((r && r.status) || '')
const ksHasLoan = r => !!(r && r.tracking === 'ks' && (Number(r.loaned_ks) || 0) > 0)

export default function PozicovnaMobile() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('dostupne')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)

  function load() {
    setLoading(true)
    fetchAuth('/api/loan-items')
      .then(r => r.ok ? r.json() : [])
      .then(d => setItems(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter(it => {
      if (!it.active) return false
      if (filter !== 'all' && effStatus(it) !== filter) return false
      if (q && !hladaj(`${it.name} ${it.code} ${it.serial_number} ${it.model} ${it.uid || ''}`, q)) return false
      return true
    })
  }, [items, filter, search])

  if (selected) {
    return <ItemDetailMobile item={selected} onBack={() => { setSelected(null); load() }} />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md, padding: SPACE.md }}>
      <h1 style={{ margin: 0, fontSize: TYPO.sizes.xl, fontWeight: TYPO.weights.extra }}>
        Požičovňa
      </h1>
      <input type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Hľadať položku, kód, výr. č…"
        style={{
          width: '100%', padding: '12px 14px',
          border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
          fontSize: TYPO.sizes.md, background: COLORS.bg, color: COLORS.text,
          boxSizing: 'border-box',
        }} />
      <div style={{ display: 'flex', gap: SPACE.xs, flexWrap: 'wrap' }}>
        {[
          { key: 'all',       label: 'Všetky' },
          { key: 'dostupne',  label: 'Dostupné' },
          { key: 'pozicane',  label: 'Požičané' },
          { key: 'v_servise', label: 'V servise' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              padding: '10px 14px',
              border: `2px solid ${filter === f.key ? COLORS.brand : COLORS.line}`,
              borderRadius: RADIUS.full,
              background: filter === f.key ? COLORS.brand : COLORS.bg,
              color: filter === f.key ? '#fff' : COLORS.text,
              fontWeight: TYPO.weights.semibold,
              fontSize: TYPO.sizes.sm,
              cursor: 'pointer', minHeight: 44,
            }}>{f.label}</button>
        ))}
      </div>
      {loading ? (
        <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>Načítavam…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>
          Žiadne položky.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
          {filtered.map(it => (
            <button key={it.id} onClick={() => setSelected(it)}
              style={{
                background: COLORS.bg, border: `1px solid ${COLORS.line}`,
                borderLeft: `4px solid ${STATUS_COLOR[effStatus(it)] || COLORS.muted}`,
                borderRadius: RADIUS.md, padding: SPACE.md,
                display: 'flex', flexDirection: 'column', gap: 4,
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                minHeight: 80,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
                <span style={{ fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.bold, flex: 1 }}>
                  {it.name}
                </span>
                <span style={{
                  fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
                  color: STATUS_COLOR[effStatus(it)],
                  padding: '2px 8px', borderRadius: RADIUS.full,
                  border: `1px solid ${STATUS_COLOR[effStatus(it)]}`,
                }}>{STATUS_LABEL[effStatus(it)]}</span>
              </div>
              <div style={{ display: 'flex', gap: SPACE.md, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                {it.code && <span style={{ fontFamily: TYPO.mono }}>{it.code}</span>}
                {it.serial_number && <span style={{ fontFamily: TYPO.mono }}>SN: {it.serial_number}</span>}
                {it.category && <span>{it.category}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ItemDetailMobile({ item, onBack }) {
  const [activeLoan, setActiveLoan] = useState(null)
  const [showLoan, setShowLoan] = useState(false)
  const [showReturn, setShowReturn] = useState(false)

  useEffect(() => {
    if (effStatus(item) === 'pozicane' || ksHasLoan(item)) {
      fetchAuth(`/api/loan-records?item_id=${item.id}&active_only=1`)
        .then(r => r.ok ? r.json() : [])
        .then(rows => setActiveLoan(rows[0] || null))
        .catch(() => {})
    }
  }, [item.id, item.status])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: SPACE.md, gap: SPACE.md }}>
      <button onClick={onBack} style={{
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: COLORS.nav, fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold,
        textAlign: 'left', padding: '8px 0', minHeight: 44,
      }}>‹ Späť na zoznam</button>
      <div style={{
        background: COLORS.bg, border: `1px solid ${COLORS.line}`,
        borderLeft: `4px solid ${STATUS_COLOR[effStatus(item)] || COLORS.muted}`,
        borderRadius: RADIUS.md, padding: SPACE.lg,
      }}>
        <h2 style={{ margin: 0, fontSize: TYPO.sizes.xl, fontWeight: TYPO.weights.extra }}>
          {item.name}
        </h2>
        <div style={{ marginTop: SPACE.sm, display: 'flex', gap: SPACE.md, flexWrap: 'wrap', fontSize: TYPO.sizes.sm }}>
          {item.code         && <Pair k="Kód"        v={item.code} mono />}
          {item.uid          && <Pair k="QR nálepka" v={item.uid} mono />}
          {item.category     && <Pair k="Kategória"  v={item.category} />}
          {item.serial_number && <Pair k="Výr. č."   v={item.serial_number} mono />}
          {item.model        && <Pair k="Model"      v={item.model} />}
          <Pair k="Stav" v={STATUS_LABEL[effStatus(item)]} color={STATUS_COLOR[effStatus(item)]} />
        </div>
        {item.notes && (
          <div style={{ marginTop: SPACE.md, padding: SPACE.sm,
            background: COLORS.bgSoft, borderRadius: RADIUS.sm,
            fontSize: TYPO.sizes.sm, whiteSpace: 'pre-wrap' }}>{item.notes}</div>
        )}
      </div>
      {activeLoan && (
        <div style={{
          background: 'rgba(245,158,11,0.08)', border: `1px solid #f59e0b`,
          borderRadius: RADIUS.md, padding: SPACE.md,
        }}>
          <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
            color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.06em',
            marginBottom: SPACE.xs }}>Aktívna výpožička</div>
          <div style={{ fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold }}>
            {activeLoan.loan_type === 'intern'
              ? `${activeLoan.employee_name || activeLoan.employee_id}`
              : `${activeLoan.customer_name || activeLoan.customer_id}`}
          </div>
          {activeLoan.project_id && (
            <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, marginTop: 2 }}>
              {activeLoan.project_name || activeLoan.project_id}
            </div>
          )}
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>
            Od {activeLoan.start_date}{activeLoan.end_date && ` · plán návrat ${activeLoan.end_date}`}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
        {effStatus(item) === 'dostupne' && (
          <button onClick={() => setShowLoan(true)} style={bigBtn(COLORS.add)}>
            Vypožičať
          </button>
        )}
        {(effStatus(item) === 'pozicane' || ksHasLoan(item)) && (
          <button onClick={() => setShowReturn(true)} style={bigBtn('#f59e0b')}>
            Vrátiť
          </button>
        )}
      </div>
      {showLoan && (
        <LoanMobile item={item} onClose={() => setShowLoan(false)}
          onDone={() => { setShowLoan(false); onBack() }} />
      )}
      {showReturn && (
        <ReturnMobile item={item} activeLoan={activeLoan} onClose={() => setShowReturn(false)}
          onDone={() => { setShowReturn(false); onBack() }} />
      )}
    </div>
  )
}

function Pair({ k, v, mono, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</span>
      <span style={{
        fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold,
        fontFamily: mono ? TYPO.mono : 'inherit',
        color: color || COLORS.text,
      }}>{v}</span>
    </div>
  )
}

const bigBtn = (color) => ({
  background: color, color: '#fff', border: 'none',
  borderRadius: RADIUS.md, padding: '16px 20px',
  fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.bold,
  cursor: 'pointer', fontFamily: 'inherit', minHeight: 56,
  width: '100%',
})

function LoanMobile({ item, onClose, onDone }) {
  const [employees, setEmployees] = useState([])
  const [projects, setProjects] = useState([])
  const [empId, setEmpId] = useState('')
  const [projId, setProjId] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  useEffect(() => {
    fetchAuth('/api/employees').then(r => r.ok ? r.json() : []).then(d => setEmployees(Array.isArray(d) ? d : (d.rows || []))).catch(() => {})
    fetchAuth('/api/projects').then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : (d.rows || []))).catch(() => {})
  }, [])
  async function save() {
    if (!empId || !projId) { toast.error('Vyber parťáka a zákazku'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/loan-records', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_id: item.id, loan_type: 'intern',
          employee_id: empId, project_id: projId,
          start_date: todayLocal(),
        }),
      })
      if (!r.ok) throw new Error('failed')
      toast.success('Vypožičané')
      if (onDone) onDone()
    } catch (e) { toast.error('Chyba') } finally { setBusy(false) }
  }
  return <MobileSheet title={`Vypožičať — ${item.name}`} onClose={onClose}>
    <MobileSelect label="Parťák" value={empId} onChange={setEmpId}
      options={employees.filter(e => e.active && e.is_partak).map(e => ({ value: e.id, label: e.name + (e.party ? ` · ${e.party}` : '') }))} />
    <MobileSelect label="Zákazka" value={projId} onChange={setProjId}
      options={projects.filter(p => p.status === 'active' || p.status === 'planned').map(p => ({ value: p.id, label: p.id + ' · ' + (p.name || '') }))} />
    <button onClick={save} disabled={busy} style={{ ...bigBtn(COLORS.add), marginTop: SPACE.md }}>
      {busy ? 'Ukladám…' : '✓ Vypožičať'}
    </button>
  </MobileSheet>
}

function ReturnMobile({ item, activeLoan, onClose, onDone }) {
  const [damaged, setDamaged] = useState(false)
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  async function save() {
    if (!activeLoan) { toast.error('Žiadna výpožička'); return }
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/loan-records/${activeLoan.id}/return`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ damaged, return_date: todayLocal() }),
      })
      if (!r.ok) throw new Error('failed')
      toast.success(damaged ? 'Vrátené (poškodené → V servise)' : 'Vrátené')
      if (onDone) onDone()
    } catch (e) { toast.error('Chyba') } finally { setBusy(false) }
  }
  return <MobileSheet title={`Vrátiť — ${item.name}`} onClose={onClose}>
    <label style={{
      display: 'flex', alignItems: 'center', gap: SPACE.md,
      padding: SPACE.md, border: `2px solid ${damaged ? COLORS.remove : COLORS.line}`,
      borderRadius: RADIUS.md, background: damaged ? 'rgba(239,68,68,0.08)' : 'transparent',
      cursor: 'pointer', minHeight: 56,
    }}>
      <input type="checkbox" checked={damaged} onChange={e => setDamaged(e.target.checked)}
        style={{ width: 24, height: 24 }} />
      <span style={{ fontSize: TYPO.sizes.md, color: damaged ? COLORS.remove : COLORS.text, fontWeight: TYPO.weights.semibold }}>
        Vrátené poškodené
      </span>
    </label>
    <button onClick={save} disabled={busy} style={{ ...bigBtn('#f59e0b'), marginTop: SPACE.md }}>
      {busy ? 'Ukladám…' : '✓ Vrátiť'}
    </button>
  </MobileSheet>
}

function MobileSheet({ title, children, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'flex-end', zIndex: 1000,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{
          background: COLORS.bg, width: '100%', maxWidth: 640, margin: '0 auto',
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          padding: `${SPACE.lg}px ${SPACE.md}px ${SPACE.xl}px`,
          maxHeight: '85vh', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: SPACE.md,
        }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h3 style={{ margin: 0, flex: 1, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra }}>
            {title}
          </h3>
          <button onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 24, color: COLORS.muted, padding: '4px 12px', minHeight: 44 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function MobileSelect({ label, value, options, onChange }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: SPACE.xs }}>
      <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: TYPO.weights.semibold, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{
          padding: '12px 14px', fontSize: TYPO.sizes.md,
          border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
          background: COLORS.bg, color: COLORS.text, minHeight: 48,
        }}>
        <option value="">— vyber —</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )
}
